import {useCallback, useEffect, useMemo, useState} from 'react';

import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import {Typography} from '@mui/material';
import {Box} from '@mui/system';
import {useTranslation} from 'react-i18next';
import {useNavigate, useSearchParams} from 'react-router-dom';

import {PaymentLinkLogo} from '@/app/main/paymentLink/components/PaymentLinkLogo/PaymentLinkLogo';
import {showSnackbar} from '@/app/shared-components/Snackbar/SnackbarProvider';
import {SnackbarType} from '@/app/shared-components/Snackbar/SnackbarTypes';
import {IRecordGroupExpenseRequest} from '@/dataSource/src/api/groups/entities';

import {SplitsPromoCard} from '../splits/components/SplitsPromoCard';

import {GroupAddExpenseForm} from './components/GroupAddExpenseForm/GroupAddExpenseForm';
import {GroupsDashboardBalanceDetail} from './components/GroupDashboardBalanceDetail/GroupDashboardBalanceDetail';
import {GroupsDashboardGeneral} from './components/GroupDashboardGeneral/GroupDashboardGeneral';
import {
  GroupsViewModelProvider,
  useGroupsViewModelProvider,
} from './GroupsViewModelContext';
import './styles';
import {
  GROUPS_DASHBOARD_STEP,
  GROUPS_ROUTES,
  GroupsDashboardStep,
} from './models/constants';
import {ESplitBillBalanceMode, ISettlementMember} from './models/interfaces';
import {getBalanceItem} from './utils/getBalanceItem';

function GroupsDashboardContent() {
  const {
    groupName,
    groupEmoji,
    handleOpenAccountDashboard,
    handleShare,
    groupCurrentParticipant,
    members,
    handleAddExpenseManualConfirm,
    balances,
    isLoadingGroup,
    ownerGroupParticipant,
    handleSettleMembers,
    trackSelectSplitsAddExpense,
  } = useGroupsViewModelProvider();
  const {t} = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [step, setStep] = useState<GroupsDashboardStep>(
    GROUPS_DASHBOARD_STEP.GENERAL,
  );
  const [oweMode, setOweMode] = useState<ESplitBillBalanceMode>(
    ESplitBillBalanceMode.OWED,
  );

  const handleBack = useCallback(() => {
    if (
      step === GROUPS_DASHBOARD_STEP.DETAIL ||
      step === GROUPS_DASHBOARD_STEP.ADD_EXPENSE
    ) {
      setStep(GROUPS_DASHBOARD_STEP.GENERAL);
    }
  }, [step]);

  const goToAddExpense = useCallback(() => {
    trackSelectSplitsAddExpense();
    setStep(GROUPS_DASHBOARD_STEP.ADD_EXPENSE);
  }, [trackSelectSplitsAddExpense]);

  const handleAddExpenseOnSubmit = useCallback(
    async (payload: IRecordGroupExpenseRequest) => {
      const result = await handleAddExpenseManualConfirm(payload);
      if (result) {
        showSnackbar({
          summary: t('groups.dashboard.expense_added.title'),
          message: t('groups.dashboard.expense_added.message'),
          variant: SnackbarType.SUCCESS,
        });
        setStep(GROUPS_DASHBOARD_STEP.GENERAL);
      } else {
        showSnackbar({
          summary: t('groups.dashboard.expense_add_error.title'),
          message: t('groups.dashboard.expense_add_error.message'),
          variant: SnackbarType.ERROR,
        });
      }
    },
    [handleAddExpenseManualConfirm, t],
  );

  const canGoBack = useMemo(() => {
    return (
      step === GROUPS_DASHBOARD_STEP.DETAIL ||
      step === GROUPS_DASHBOARD_STEP.ADD_EXPENSE
    );
  }, [step]);

  const isAddExpenseStep = step === GROUPS_DASHBOARD_STEP.ADD_EXPENSE;
  const showGroupIcon = useMemo(() => {
    return isAddExpenseStep && !!groupEmoji;
  }, [isAddExpenseStep, groupEmoji]);

  const balancesItems = useMemo(() => {
    const owedAmount = balances?.owed.amount ?? 0;
    const owedMembers = balances?.owed.members ?? [];
    const oweAmount = balances?.owe.amount ?? 0;
    const oweMembers = balances?.owe.members ?? [];

    return {
      owed: getBalanceItem(
        owedAmount,
        owedMembers,
        'groups.dashboard.you_owed',
        'groups.dashboard.owed_members',
        'groups.dashboard.owed_zero',
        t,
      ),
      owe: getBalanceItem(
        oweAmount,
        oweMembers,
        'groups.dashboard.you_owe',
        'groups.dashboard.owe_members',
        'groups.dashboard.owe_zero',
        t,
      ),
    };
  }, [balances, t]);

  const handleBalanceClick = useCallback((type: ESplitBillBalanceMode) => {
    setOweMode(type);
    setStep(GROUPS_DASHBOARD_STEP.DETAIL);
  }, []);

  const handleOnSettleMembers = useCallback(
    async (members: ISettlementMember[]) => {
      await handleSettleMembers(members);
      navigate(`${GROUPS_ROUTES.SUCCESS}?${searchParams.toString()}`);
    },
    [handleSettleMembers, navigate, searchParams],
  );

  const renderStep = useCallback(() => {
    switch (step) {
      case GROUPS_DASHBOARD_STEP.GENERAL:
        return (
          <GroupsDashboardGeneral
            isLoadingGroup={isLoadingGroup}
            groupEmoji={groupEmoji}
            groupName={groupName}
            balancesItems={balancesItems}
            members={members}
            handleShare={handleShare}
            handleAddExpense={goToAddExpense}
            onBalanceClick={handleBalanceClick}
            groupCurrentParticipant={groupCurrentParticipant}
            ownerGroupParticipant={ownerGroupParticipant}
          />
        );
      case GROUPS_DASHBOARD_STEP.DETAIL:
        return (
          <GroupsDashboardBalanceDetail
            groupCurrentParticipant={groupCurrentParticipant}
            ownerGroupParticipant={ownerGroupParticipant}
            oweMode={oweMode}
            balances={balances}
            onSettle={handleOnSettleMembers}
          />
        );
      case GROUPS_DASHBOARD_STEP.ADD_EXPENSE:
        return (
          <GroupAddExpenseForm
            groupName={groupName}
            inviterName={groupCurrentParticipant?.name || ''}
            members={members}
            currentUserId={groupCurrentParticipant?.splitUserId}
            onSubmit={handleAddExpenseOnSubmit}
          />
        );
    }
  }, [
    balances,
    balancesItems,
    goToAddExpense,
    groupCurrentParticipant,
    groupEmoji,
    groupName,
    handleAddExpenseOnSubmit,
    handleBalanceClick,
    handleOnSettleMembers,
    handleShare,
    isLoadingGroup,
    members,
    oweMode,
    ownerGroupParticipant,
    step,
  ]);

  useEffect(() => {
    if (isLoadingGroup) return;

    if (!groupCurrentParticipant) {
      navigate(`${GROUPS_ROUTES.LANDING}?${searchParams.toString()}`);
    }
  }, [isLoadingGroup, groupCurrentParticipant, navigate, searchParams]);

  return (
    <section
      className="splits-landing"
      style={{
        paddingLeft: 16,
        paddingRight: 16,
      }}>
      <Box className="splits-landing-container">
        <Box className="splits-landing-logo">
          {canGoBack && (
            <ArrowBackIosNewIcon
              id="GroupsDashboard_backIcon"
              onClick={handleBack}
              style={{cursor: 'pointer'}}
            />
          )}
          <Box className="logo-container">
            {showGroupIcon ? (
              <Typography className="group-add-expense-title">
                {t('groups.dashboard.add_expense')}
              </Typography>
            ) : (
              <PaymentLinkLogo />
            )}
          </Box>
          {showGroupIcon && (
            <Box
              id="GroupsDashboard_groupIcon"
              className="groups-dashboard-nav-group-icon"
              aria-label={groupName}>
              {groupEmoji}
            </Box>
          )}
        </Box>
      </Box>
      <Box className="groups-dashboard-container">
        {renderStep()}
        <Box className="groups-dashboard-separator-line"></Box>
        <SplitsPromoCard
          inApp={false}
          handleSplitWithMo={null}
          handleOpenAccount={handleOpenAccountDashboard}
        />
      </Box>
    </section>
  );
}

const GroupsDashboardPage = () => (
  <GroupsViewModelProvider>
    <GroupsDashboardContent />
  </GroupsViewModelProvider>
);

export default GroupsDashboardPage;
